import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { auditsApi } from '../services/audits';
import { formatDate } from '../i18n/format';
import CostEstimate from '../components/billing/CostEstimate';
import AxisScheduleControl from '../components/brand/AxisScheduleControl';
import AuditCapConfig from '../components/brand/AuditCapConfig';
import HowItWorksCard from '../components/HowItWorksCard';
import type { Audit, WebsiteAgentType, AgentStatus } from '@traaker/shared';
import { websiteAgentsForTier } from '@traaker/shared';

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  pending: 'audits.status.pending',
  running: 'audits.status.running',
  completed: 'audits.status.completed',
  failed: 'audits.status.failed',
};

const AGENT_LABEL_KEYS: Record<WebsiteAgentType, string> = {
  crawl: 'audits.agent.crawl',
  content_freshness: 'audits.agent.contentFreshness',
  structural_seo: 'audits.agent.structuralSeo',
  content_quality: 'audits.agent.contentQuality',
};

const AGENT_STATUS_COLORS: Record<AgentStatus, string> = {
  pending: '#A0A4AE',
  running: '#F59E0B',
  completed: '#10B981',
  failed: '#EF4444',
};

const AGENT_KEYS: WebsiteAgentType[] = ['crawl', 'content_freshness', 'structural_seo', 'content_quality'];

export default function Audits() {
  const { tenant } = useAuth();
  const { activeBrand, refreshBrands } = useBrand();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Manual run-now is an agency feature; self-serve/enterprise are cadence-driven
  // (the API enforces this too, allowing only the onboarding first-run).
  const isAgency = tenant?.tier === 'agency';
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const brandDomain = activeBrand?.url
    ? (() => { try { return new URL(activeBrand.url).hostname; } catch { return activeBrand.url; } })()
    : tenant?.domain;

  const loadAudits = async () => {
    try {
      const data = await auditsApi.list('website', activeBrand?.brandId);
      setAudits(data);
    } catch {
      setError(t('audits.error.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadAudits();
  }, [activeBrand?.brandId]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      await auditsApi.create('website', {}, activeBrand?.brandId);
      setDialogOpen(false);
      await loadAudits();
    } catch (err: any) {
      setError(err.message || t('audits.error.create'));
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700} data-tour="page-intro">
          {t('audits.title')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeBrand?.brandId && (
            <Box component="span" data-tour="schedule-control" sx={{ display: 'inline-flex' }}>
              <AxisScheduleControl
                brandId={activeBrand.brandId}
                tier={tenant?.tier || ''}
                axis="website"
                cadenceOverride={activeBrand.auditConfig?.cadenceOverride}
                onSaved={refreshBrands}
              />
            </Box>
          )}
          {isAgency && activeBrand?.brandId && (
            <Box component="span" data-tour="cap-control" sx={{ display: 'inline-flex' }}>
              <AuditCapConfig
                brandId={activeBrand.brandId}
                kind="website"
                value={activeBrand.auditConfig?.maxPagesPerWebsiteAudit}
                onSaved={refreshBrands}
              />
            </Box>
          )}
          {isAgency && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              {t('audits.newAudit')}
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <HowItWorksCard
        title={t('audits.howItWorks.title')}
        steps={[
          {
            title: t('audits.howItWorks.discover.title'),
            description: t('audits.howItWorks.discover.description'),
            image: '/assets/how-it-works/website-audit-discover.webp',
          },
          {
            title: t('audits.howItWorks.select.title'),
            description: t('audits.howItWorks.select.description'),
            image: '/assets/how-it-works/website-audit-select.webp',
          },
          {
            title: t('audits.howItWorks.score.title'),
            description: t('audits.howItWorks.score.description'),
            image: '/assets/how-it-works/website-audit-score.webp',
          },
        ]}
      />

      {audits.length === 0 ? (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('audits.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {isAgency
                ? t('audits.empty.agency', { domain: brandDomain || t('audits.empty.yourSite') })
                : t('audits.empty.selfServe')}
            </Typography>
            {isAgency && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
                {t('audits.empty.launch')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('audits.col.date')}</TableCell>
                <TableCell>{t('audits.col.pagesAnalyzed')}</TableCell>
                <TableCell>{t('audits.col.status')}</TableCell>
                <TableCell>{t('audits.col.agents')}</TableCell>
                <TableCell>{t('audits.col.scoreWebsite')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audits.map((audit) => (
                <TableRow
                  key={audit.auditId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/audits/${audit.auditId}`)}
                >
                  <TableCell>
                    {formatDate(audit.createdAt, {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell>
                    {audit.websiteSummary?.pagesAnalyzed ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABEL_KEYS[audit.status] ? t(STATUS_LABEL_KEYS[audit.status]) : audit.status}
                      color={STATUS_COLORS[audit.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {AGENT_KEYS.filter((agent) => websiteAgentsForTier(tenant?.tier).includes(agent)).map((agent) => {
                        const status = audit.agentStatuses?.[agent] || 'pending';
                        const statusLabel = STATUS_LABEL_KEYS[status] ? t(STATUS_LABEL_KEYS[status]) : status;
                        return (
                          <Tooltip key={agent} title={`${t(AGENT_LABEL_KEYS[agent])}: ${statusLabel}`}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: AGENT_STATUS_COLORS[status] || '#555',
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {audit.websiteSummary?.overallGeoScore ?? '—'}
                      {audit.websiteSummary?.overallGeoScore !== undefined && '/100'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('audits.dialog.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body1">
              {t('audits.dialog.launchPrefix')}{' '}
              <strong>{brandDomain || activeBrand?.name || '—'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('audits.dialog.description')}
            </Typography>
            <CostEstimate
              auditType="website"
              maxPagesOverride={activeBrand?.auditConfig?.maxPagesPerWebsiteAudit}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>{t('audits.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : t('audits.launchAudit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
